import React, { useEffect, useRef, useState } from "react";
import Header from "../Header";
import Choices from "choices.js";

const TIMEZONES = [
  { value: "UTC-12", label: "UTC - 12:00" },
  { value: "UTC-11", label: "UTC - 11:00" },
  { value: "UTC-10", label: "UTC - 10:00" },
  { value: "UTC-9:30", label: "UTC - 09:30" },
  { value: "UTC-9", label: "UTC - 09:00" },
  { value: "UTC-8", label: "UTC - 08:00" },
  { value: "UTC-7", label: "UTC - 07:00" },
  { value: "UTC-6", label: "UTC - 06:00" },
  { value: "UTC-5", label: "UTC - 05:00" },
  { value: "UTC-4", label: "UTC - 04:00" },
  { value: "UTC-3:30", label: "UTC - 03:30" },
  { value: "UTC-3", label: "UTC - 03:00" },
  { value: "UTC-2", label: "UTC - 02:00" },
  { value: "UTC-1", label: "UTC - 01:00" },
  { value: "UTC+0", label: "UTC + 00:00" },
  { value: "UTC+1", label: "UTC + 01:00" },
  { value: "UTC+2", label: "UTC + 02:00" },
  { value: "UTC+3", label: "UTC + 03:00" },
  { value: "UTC+3:30", label: "UTC + 03:30" },
  { value: "UTC+4", label: "UTC + 04:00" },
  { value: "UTC+4:30", label: "UTC + 04:30" },
  { value: "UTC+5", label: "UTC + 05:00" },
  { value: "UTC+5:30", label: "UTC + 05:30" },
  { value: "UTC+5:40", label: "UTC + 05:45" },
  { value: "UTC+6", label: "UTC + 06:00" },
  { value: "UTC+6:30", label: "UTC + 06:30" },
  { value: "UTC+7", label: "UTC + 07:00" },
  { value: "UTC+8", label: "UTC + 08:00" },
  { value: "UTC+8:45", label: "UTC + 08:45" },
  { value: "UTC+9", label: "UTC + 09:00" },
  { value: "UTC+9:30", label: "UTC + 09:30" },
  { value: "UTC+10", label: "UTC + 10:00" },
  { value: "UTC+10:30", label: "UTC + 10:30" },
  { value: "UTC+11", label: "UTC + 11:00" },
  { value: "UTC+12", label: "UTC + 12:00" },
  { value: "UTC+12:45", label: "UTC + 12:45" },
  { value: "UTC+13", label: "UTC + 13:00" },
  { value: "UTC+14", label: "UTC + 14:00" },
];

const ODDS_OPTIONS = [
  { value: "decimal", label: "Decimal Odds (1.50)" },
  { value: "fractional", label: "Fractional Odds (1/2)" },
];

const Settings = () => {
  const timeZoneRef = useRef(null);
  const oddsRef = useRef(null);
  const timeZoneChoices = useRef(null);
  const oddsChoices = useRef(null);

  const [selectedTimeZone, setSelectedTimeZone] = useState("UTC+0");
  const [selectedOdds, setSelectedOdds] = useState("decimal");
  const [successMessage, setSuccessMessage] = useState("");

  // Load from localStorage once on mount
  useEffect(() => {
    const storedTZ = localStorage.getItem("selectedTimeZone");
    const storedOdds = localStorage.getItem("selectedOdds");

    if (storedTZ && TIMEZONES.some((tz) => tz.value === storedTZ)) {
      setSelectedTimeZone(storedTZ);
    }

    if (storedOdds && ODDS_OPTIONS.some((od) => od.value === storedOdds)) {
      setSelectedOdds(storedOdds);
    }
  }, []);

  // Initialize Choices once, after first render
  useEffect(() => {
    if (timeZoneRef.current && !timeZoneChoices.current) {
      timeZoneChoices.current = new Choices(timeZoneRef.current, {
        searchEnabled: true,
        removeItemButton: false,
        placeholderValue: "Select Timezone",
        noResultsText: "Not found",
        noChoicesText: "Not available",
        searchPlaceholderValue: "Search Timezone",
        itemSelectText: "",
        shouldSort: false,
      });

      timeZoneRef.current.addEventListener("change", (e) => {
        setSelectedTimeZone(e.target.value);
      });
    }

    if (oddsRef.current && !oddsChoices.current) {
      oddsChoices.current = new Choices(oddsRef.current, {
        searchEnabled: false,
        removeItemButton: false,
        shouldSort: false,
        itemSelectText: "",
        allowHTML: true,
      });

      oddsRef.current.addEventListener("change", (e) => {
        setSelectedOdds(e.target.value);
      });
    }

    return () => {
      // Cleanup listeners and destroy Choices on unmount
      if (timeZoneRef.current) {
        timeZoneRef.current.removeEventListener("change", () => {});
      }
      if (oddsRef.current) {
        oddsRef.current.removeEventListener("change", () => {});
      }
      if (timeZoneChoices.current) {
        timeZoneChoices.current.destroy();
        timeZoneChoices.current = null;
      }
      if (oddsChoices.current) {
        oddsChoices.current.destroy();
        oddsChoices.current = null;
      }
    };
  }, []);

  // Update Choices selections programmatically whenever state changes
  useEffect(() => {
    if (timeZoneChoices.current) {
      timeZoneChoices.current.setChoiceByValue(selectedTimeZone);
    }
  }, [selectedTimeZone]);

  useEffect(() => {
    if (oddsChoices.current) {
      oddsChoices.current.setChoiceByValue(selectedOdds);
    }
  }, [selectedOdds]);

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem("selectedTimeZone", selectedTimeZone);
    localStorage.setItem("selectedOdds", selectedOdds);
    setSuccessMessage("Preferences saved!");
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  return (
    <div className="body">
      <Header />
      <div className="wrapper">
        <main className="page">
          <section className="settings">
            <div className="container-fix-width">
              <div className="settings-wrap container-styles">
                <h1 className="main-title">Settings</h1>
                <form
                  onSubmit={handleSubmit}
                  className="settings-form form-styles"
                >
                  <div className="filter-select-wrap settings__select-wrap">
                    <label htmlFor="timeZ">Time Zone</label>
                    <div className="set-select__wrap">
                      <select
                        id="timeZ"
                        ref={timeZoneRef}
                        defaultValue={selectedTimeZone}
                      >
                        {TIMEZONES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="settings__custom-select">
                    <label htmlFor="oddsOptions">ODDS</label>
                    <div className="set-select__wrap">
                      <select
                        id="oddsOptions"
                        name="oddsOptions"
                        ref={oddsRef}
                        defaultValue={selectedOdds}
                      >
                        {ODDS_OPTIONS.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button type="submit" className="main-btn" name="settingsBtn">
                    Save Changes
                  </button>
                  {successMessage && (
                    <div
                      style={{
                        marginTop: "15px",
                        borderRadius: "12px",
                        color: "#065f46",
                      }}
                    >
                      ✅ {successMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Settings;
